import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { pushLog } from '@/libs/debug-log/log-store';
import { safeStringify } from '@/libs/debug-log/serialize';
import { enqueueFirebaseIdTokenRevokedTeardown } from '@/libs/api/firebase-cloud-functions-scope';
import type { ApiError, TokenProvider } from '@/libs/api/types';
import { getCurrentEnvUrls } from '@/libs/config/env-urls';
import { isAxiosTimeoutError, showRequestTimeoutAlert } from '@/libs/errors/request-timeout';
import { captureException } from '@/libs/logger';
import { ensureOnline, isAxiosNetworkError, isOnline, showOfflineAlert } from '@/libs/network';
import { useUiStore } from '@/stores';
import axios, {
  AxiosError,
  type AxiosInstance,
  CanceledError,
  type InternalAxiosRequestConfig,
} from 'axios';

export const ID_TOKEN_REVOKED = 'auth/id-token-revoked';
export const USER_DISABLED = 'auth/user-disabled';

function readApiResponseBodyCode(responseData: unknown): string | undefined {
  if (!responseData || typeof responseData !== 'object') {
    return undefined;
  }
  const obj = responseData as Record<string, unknown>;
  const flat = obj.code;
  if (typeof flat === 'string') {
    return flat;
  }
  const nested = obj.error;
  if (
    nested &&
    typeof nested === 'object' &&
    'code' in nested &&
    typeof (nested as { code?: unknown }).code === 'string'
  ) {
    return (nested as { code: string }).code;
  }
  return undefined;
}

/**
 * Resolves `baseURL` per-request from the env store, so toggling Firebase env
 * at runtime swaps the cloud-functions backend without rebuilding the axios
 * instance.
 */
export function createCloudFunctionsBaseUrlInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      config.baseURL = getCurrentEnvUrls().firebaseCloudFunctionUrl;
      return config;
    },
  };
}

/**
 * Resolves `baseURL` per-request from the env store, so toggling Firebase env
 * at runtime swaps the cloud-functions backend without rebuilding the axios
 * instance.
 */
export function createUsKidsGolfBaseUrlInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      config.baseURL = getCurrentEnvUrls().usKidGolfUrl;
      return config;
    },
  };
}

/**
 * Resolves `baseURL` per-request from the env store for the booking-engine backend
 * (public coach-directory search), so toggling env at runtime swaps the host.
 */
export function createBookingEngineBaseUrlInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      config.baseURL = getCurrentEnvUrls().bookingEngineUrl;
      return config;
    },
  };
}

type LoggedRequestConfig = InternalAxiosRequestConfig & { __logStartTs?: number };

function resolveLoggedUrl(config?: InternalAxiosRequestConfig): string {
  if (!config) return '';
  const base = config.baseURL ?? '';
  const path = config.url ?? '';
  return `${base}${path}`;
}

/**
 * Request half of the debug logger: stamps a start time on the config so the
 * response half can compute a duration. Feeds the in-app debug log panel
 * (profile long-press), independent of Sentry. Never throws into the chain.
 */
export function createDebugLogRequestInterceptor() {
  return {
    onFulfilled: (config: InternalAxiosRequestConfig) => {
      (config as LoggedRequestConfig).__logStartTs = Date.now();
      return config;
    },
  };
}

/**
 * Response half of the debug logger. Register this *first* in the response chain
 * so its `onRejected` receives the raw `AxiosError` (with `config`/`response`)
 * before `createErrorInterceptor` normalizes it. Bodies/headers are redacted and
 * truncated by `safeStringify`. Always re-resolves/re-rejects to stay transparent.
 */
export function createDebugLogResponseInterceptor() {
  const durationOf = (config?: LoggedRequestConfig) =>
    config?.__logStartTs ? Date.now() - config.__logStartTs : undefined;

  return {
    onFulfilled: (response: import('axios').AxiosResponse) => {
      try {
        const config = response.config as LoggedRequestConfig;
        pushLog({
          kind: 'network',
          method: (config.method ?? 'get').toUpperCase(),
          url: resolveLoggedUrl(config),
          status: response.status,
          durationMs: durationOf(config),
          requestBody: config.data != null ? safeStringify(config.data) : undefined,
          responseBody: response.data != null ? safeStringify(response.data) : undefined,
        });
      } catch {
        // logging is best-effort
      }
      return response;
    },
    onRejected: (error: unknown) => {
      try {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          const config = axiosError.config as LoggedRequestConfig | undefined;
          pushLog({
            kind: 'network',
            method: (config?.method ?? 'get').toUpperCase(),
            url: resolveLoggedUrl(config),
            status: axiosError.response?.status,
            durationMs: durationOf(config),
            requestBody: config?.data != null ? safeStringify(config.data) : undefined,
            responseBody:
              axiosError.response?.data != null
                ? safeStringify(axiosError.response.data)
                : undefined,
            error: axiosError.message,
          });
        }
      } catch {
        // logging is best-effort
      }
      return Promise.reject(error);
    },
  };
}

export function createNetworkCheckInterceptor() {
  return {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      await ensureOnline();
      return config;
    },
  };
}

/**
 * Retries transient network/DNS failures (`ERR_NETWORK` with no response) a few
 * times with exponential backoff. These spike right after a cold start or a
 * RevoPush/native-update bundle reload on Android: the OkHttp DNS resolver is
 * cold and the first lookups for an uncached host fail with "No address
 * associated with hostname" even though the device is online — a sibling request
 * to a different host succeeds at the same instant. Without this, the failure
 * surfaces as a false "offline" alert (`createErrorInterceptor`) and the call
 * (e.g. `/guardians/me/roles`) never recovers until the user kills the app.
 *
 * Register this *before* `createErrorInterceptor` so a successful retry resolves
 * the chain before the offline alert fires. It only retries while NetInfo reports
 * online, so a genuine offline state falls straight through to the offline handler.
 * The retry count rides on the request config, and re-dispatching through
 * `instance.request` re-runs this same interceptor — so the count guard bounds the
 * total attempts (1 original + `retries`).
 */
export function createNetworkRetryInterceptor(
  instance: AxiosInstance,
  options?: { retries?: number; baseDelayMs?: number },
) {
  const maxRetries = options?.retries ?? 2;
  const baseDelayMs = options?.baseDelayMs ?? 500;
  return {
    onRejected: async (error: unknown) => {
      if (error instanceof CanceledError || !axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      const axiosError = error as AxiosError;
      if (!isAxiosNetworkError(axiosError)) {
        return Promise.reject(error);
      }

      const config = axiosError.config as
        | (InternalAxiosRequestConfig & { __networkRetryCount?: number })
        | undefined;
      if (!config) {
        return Promise.reject(error);
      }

      const attempt = config.__networkRetryCount ?? 0;
      if (attempt >= maxRetries) {
        return Promise.reject(error);
      }

      // Genuine offline → don't burn retries; let the offline handler take over.
      if (!(await isOnline())) {
        return Promise.reject(error);
      }

      config.__networkRetryCount = attempt + 1;
      const delay = baseDelayMs * 2 ** attempt;
      await new Promise(resolve => setTimeout(resolve, delay));
      return instance.request(config);
    },
  };
}

export function createAuthInterceptor(tokenProvider: TokenProvider) {
  return {
    onFulfilled: async (config: InternalAxiosRequestConfig) => {
      const authConfig = config as InternalAxiosRequestConfig & {
        skipAuth?: boolean;
        authToken?: string;
      };

      if (authConfig.skipAuth) {
        return config;
      }

      // A per-request `authToken` (passed via RequestOptions) overrides the Firebase
      // ID token for this call only, without affecting other requests.
      const token = authConfig.authToken ?? (await tokenProvider.getAccessToken());
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  };
}

/**
 * Surfaces backend error messages to the user. The shared `createErrorInterceptor`
 * normalizes the response into an `ApiError` (with `message` taken from the JSON
 * body, e.g. `{ "message": "Invalid phone number" }`) but only alerts on
 * timeout/offline. This interceptor shows the server message for ordinary 4xx/5xx
 * responses. It runs *after* `createErrorInterceptor`, so it receives the
 * normalized `ApiError`, and it skips cases another layer already handled
 * (timeout/offline → `alertHandled`, token revocation → teardown).
 */
export function createServerErrorAlertInterceptor() {
  return {
    onRejected: (error: unknown) => {
      if (error instanceof CanceledError) {
        return Promise.reject(error);
      }

      const apiError = error as (ApiError & { alertHandled?: boolean }) | undefined;
      const message = typeof apiError?.message === 'string' ? apiError.message.trim() : '';
      const status = apiError?.status ?? 0;
      const shouldAlert =
        !!apiError &&
        !apiError.alertHandled &&
        apiError.serverCode !== ID_TOKEN_REVOKED &&
        status >= 400 &&
        message.length > 0;

      if (shouldAlert) {
        useUiStore.getState().hideGlobalLoading();
        showAlert({ title: STRINGS.common.alert.error, message });
        apiError.alertHandled = true;
      }

      return Promise.reject(error);
    },
  };
}

export function createErrorInterceptor() {
  return {
    onRejected: (error: unknown) => {
      if (error instanceof CanceledError) {
        return Promise.reject(error);
      }

      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      const axiosError = error as AxiosError<ApiError>;

      const serverCode = readApiResponseBodyCode(axiosError.response?.data);
      const status = axiosError.response?.status || 0;
      const responseDetails =
        axiosError.response?.data &&
        typeof axiosError.response.data === 'object' &&
        'details' in axiosError.response.data &&
        axiosError.response.data.details &&
        typeof axiosError.response.data.details === 'object'
          ? axiosError.response.data.details
          : undefined;
      const invitedEmail =
        responseDetails &&
        'invitedEmail' in responseDetails &&
        typeof responseDetails.invitedEmail === 'string'
          ? responseDetails.invitedEmail.trim()
          : undefined;

      const apiError: ApiError & { alertHandled?: boolean } = {
        message:
          (axiosError.response?.data && typeof axiosError.response.data === 'object'
            ? (axiosError.response.data as { message?: string }).message
            : undefined) ||
          axiosError.message ||
          'Unknown error',
        status,
        code: axiosError.code,
        serverCode,
        details: invitedEmail ? { invitedEmail } : undefined,
        errors: axiosError.response?.data?.errors,
        request: axiosError.request,
      };

      // Opt-out of the shared error popup for this request (RequestOptions.skipServerErrorAlert).
      // Marking it handled keeps the downstream alert interceptor quiet while still logging/rejecting.
      if (
        (axiosError.config as { skipServerErrorAlert?: boolean } | undefined)?.skipServerErrorAlert
      ) {
        apiError.alertHandled = true;
      }

      if (status === 401 && (serverCode === ID_TOKEN_REVOKED || serverCode === USER_DISABLED)) {
        enqueueFirebaseIdTokenRevokedTeardown().catch(() => {});
        return Promise.reject(apiError);
      }

      if (isAxiosTimeoutError(axiosError)) {
        useUiStore.getState().hideGlobalLoading();
        showRequestTimeoutAlert();
        apiError.alertHandled = true;
        captureException({ error_payload: apiError }, { tags: { usk_log_type: 'api_timeout' } });
        return Promise.reject(apiError);
      }

      if (isAxiosNetworkError(axiosError)) {
        useUiStore.getState().hideGlobalLoading();
        showOfflineAlert();
        apiError.alertHandled = true;
        captureException({ error_payload: apiError }, { tags: { usk_log_type: 'api_offline' } });
        return Promise.reject(apiError);
      }

      captureException(
        {
          error_payload: apiError,
        },
        { tags: { usk_log_type: 'api_error' } },
      );

      return Promise.reject(apiError);
    },
  };
}
